import { Middleware, detectOverflow } from '@floating-ui/dom';

/**
 * Determines whether a Floating UI positioning target should be considered
 * "disconnected" and therefore no longer positionable.
 *
 * The tooltip can be anchored either to a real DOM element (buttons, icons…)
 * or to a Floating UI *virtual element* — a plain object exposing
 * `getBoundingClientRect` but no DOM properties (this is how the mapbox tooltip
 * anchors to a moving lng/lat point that is not part of the DOM).
 *
 * We only treat a target as disconnected when it is a real DOM node whose
 * `isConnected` is explicitly `false` (e.g. a button removed during route
 * navigation). Virtual elements have no `isConnected` property (`undefined`),
 * so the strict `=== false` check leaves them positionable.
 */
export function isDisconnectedTarget(target: HTMLElement): boolean {
  return target.isConnected === false;
}

/**
 * Shrinks the reference rect to the part of the target that its clipping ancestors and the viewport
 * leave on screen. Floating UI anchors to the full rect, so a tall element scrolled partly out of a
 * scroll container (a calendar event, say) would get its tooltip at its hidden edge, over whatever
 * is laid out above the container. A target clipped out of view entirely keeps its full rect.
 *
 * Clipping goes through `detectOverflow` rather than a walk of the overflow ancestors so that an
 * ancestor which does not clip the target — one a `position: fixed` modal escapes, say — is left
 * out, and the clip is each ancestor's padding box, not its border box and scrollbar.
 *
 * It clips again on every pass instead of once: `size` resets the rects to the target's full ones
 * whenever it resizes the tooltip.
 */
export function clipReferenceToVisible(): Middleware {
  return {
    name: 'clipReferenceToVisible',
    async fn(state) {
      const overflow = await detectOverflow(state, { elementContext: 'reference' });
      const top = Math.max(0, overflow.top);
      const bottom = Math.max(0, overflow.bottom);
      const left = Math.max(0, overflow.left);
      const right = Math.max(0, overflow.right);
      const { reference, floating } = state.rects;
      const width = reference.width - left - right;
      const height = reference.height - top - bottom;
      // The sub-pixel floor keeps rounding in scaled coordinates from resetting on every pass.
      if (Math.max(top, bottom, left, right) < 0.5 || width <= 0 || height <= 0) {
        return {};
      }
      return {
        reset: {
          rects: {
            floating,
            reference: { x: reference.x + left, y: reference.y + top, width, height }
          }
        }
      };
    }
  };
}
